import React, { useState, useEffect } from "react";
import { api, BusinessUnit } from "../api";

const FM_LABELS: Record<number, string> = { 1:"Apr", 2:"May", 3:"Jun", 4:"Jul", 5:"Aug", 6:"Sep", 7:"Oct", 8:"Nov", 9:"Dec", 10:"Jan", 11:"Feb", 12:"Mar" };

const AdminBusinessUnits: React.FC = () => {
  const [units, setUnits] = useState<BusinessUnit[]>([]);
  const [loading, setLoading] = useState(true);

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [addCode, setAddCode] = useState("");
  const [addName, setAddName] = useState("");
  const [addFY, setAddFY] = useState(() => {
    const now = new Date();
    const yr = now.getFullYear();
    return now.getMonth() < 3 ? `FY${String(yr - 1).slice(-2)}` : `FY${String(yr).slice(-2)}`;
  });
  const [addStartMonth, setAddStartMonth] = useState<number | null>(null);
  const [addError, setAddError] = useState("");

  // Edit
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editCode, setEditCode] = useState("");
  const [editName, setEditName] = useState("");
  const [editFY, setEditFY] = useState("");
  const [editStartMonth, setEditStartMonth] = useState<number | null>(null);

  useEffect(() => { loadBUs(); }, []);

  const loadBUs = () => {
    setLoading(true);
    api.get("/business-units")
      .then(res => { setUnits(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const handleAdd = async () => {
    if (!addCode.trim() || !addName.trim()) { setAddError("Code and name required"); return; }
    try {
      await api.post("/business-units", {
        code: addCode.trim(),
        name: addName.trim(),
        activated_fy: addFY.trim() || null,
        start_fiscal_month: addStartMonth,
      });
      setShowAdd(false);
      setAddCode("");
      setAddName("");
      setAddFY(() => {
        const now = new Date();
        const yr = now.getFullYear();
        return now.getMonth() < 3 ? `FY${String(yr - 1).slice(-2)}` : `FY${String(yr).slice(-2)}`;
      });
      setAddStartMonth(null);
      setAddError("");
      loadBUs();
    } catch (err: any) {
      setAddError(err.response?.data?.error || "Failed to create");
    }
  };

  const handleSaveEdit = async (id: number) => {
    try {
      await api.put(`/business-units/${id}`, {
        code: editCode.trim(),
        name: editName.trim(),
        activated_fy: editFY.trim() || null,
        start_fiscal_month: editStartMonth,
      });
      setEditingId(null);
      loadBUs();
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to update");
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete "${name}"? This will fail if cost centers are still linked.`)) return;
    try {
      await api.delete(`/business-units/${id}`);
      loadBUs();
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to delete");
    }
  };

  if (loading) return <div style={{ padding: 20, color: "#9ca3af" }}>Loading business units...</div>;

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <span className="material-icons">business</span>
        <span className="admin-section-title">Business Units</span>
        <span className="admin-section-subtitle">{units.length} Business Unit{units.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="admin-section-body">
        {/* Toolbar */}
        <div className="admin-cc-toolbar">
          <button className="admin-btn admin-btn--primary" onClick={() => setShowAdd(!showAdd)}>
            <span className="material-icons">{showAdd ? "close" : "add"}</span>
            {showAdd ? "Cancel" : "Add Business Unit"}
          </button>
        </div>

        {/* Add Form */}
        {showAdd && (
          <div className="admin-add-form">
            <input
              className="admin-text-input"
              placeholder="Code (e.g. CORP)"
              value={addCode}
              onChange={e => setAddCode(e.target.value)}
              style={{ maxWidth: 160 }}
            />
            <input
              className="admin-text-input"
              placeholder="Name (e.g. Central - CORP)"
              value={addName}
              onChange={e => setAddName(e.target.value)}
              style={{ flex: 1 }}
            />
            <input
              className="admin-text-input"
              placeholder="FY (e.g. FY26)"
              value={addFY}
              onChange={e => setAddFY(e.target.value)}
              style={{ maxWidth: 80 }}
            />
            <select
              className="admin-select"
              value={addStartMonth ?? ""}
              onChange={e => setAddStartMonth(e.target.value ? Number(e.target.value) : null)}
              style={{ maxWidth: 130 }}
            >
              <option value="">Start Month…</option>
              <option value="1">April (M1)</option>
              <option value="2">May (M2)</option>
              <option value="3">June (M3)</option>
              <option value="4">July (M4)</option>
              <option value="5">August (M5)</option>
              <option value="6">September (M6)</option>
              <option value="7">October (M7)</option>
              <option value="8">November (M8)</option>
              <option value="9">December (M9)</option>
              <option value="10">January (M10)</option>
              <option value="11">February (M11)</option>
              <option value="12">March (M12)</option>
            </select>
            <button className="admin-btn admin-btn--success" onClick={handleAdd}>
              <span className="material-icons">check</span> Create
            </button>
            {addError && <span className="admin-toast admin-toast--err">{addError}</span>}
          </div>
        )}

        {/* Table */}
        <table className="admin-cc-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th style={{ textAlign: "center" }}>Cost Centers</th>
              <th>Activated FY</th>
              <th>Start Month</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {units.map(bu => (
              <tr key={bu.id}>
                {editingId === bu.id ? (
                  <>
                    <td>
                      <input
                        className="admin-text-input"
                        value={editCode}
                        onChange={e => setEditCode(e.target.value)}
                        style={{ maxWidth: 140 }}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-text-input"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                      />
                    </td>
                    <td style={{ textAlign: "center" }}>{bu.cc_count || 0}</td>
                    <td>
                      <input className="admin-text-input" value={editFY} onChange={e => setEditFY(e.target.value)} style={{ maxWidth: 70, fontSize: "12px" }} placeholder="FY26" />
                    </td>
                    <td>
                      <select className="admin-select" value={editStartMonth ?? ""} onChange={e => setEditStartMonth(e.target.value ? Number(e.target.value) : null)} style={{ fontSize: "12px" }}>
                        <option value="">—</option>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{FM_LABELS[m]}</option>)}
                      </select>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div className="admin-btn-row" style={{ justifyContent: "flex-end" }}>
                        <button className="admin-btn admin-btn--success" onClick={() => handleSaveEdit(bu.id)}>Save</button>
                        <button className="admin-btn admin-btn--ghost" onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td><span className="admin-cc-num">{bu.code}</span></td>
                    <td>{bu.name}</td>
                    <td style={{ textAlign: "center", color: "#64748b" }}>{bu.cc_count || 0}</td>
                    <td>{bu.activated_fy || "—"}</td>
                    <td>{bu.start_fiscal_month ? FM_LABELS[bu.start_fiscal_month] : "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      <div className="admin-btn-row" style={{ justifyContent: "flex-end" }}>
                        <button
                          className="admin-btn admin-btn--icon admin-btn--ghost"
                          title="Edit business unit code, name, and activation"
                          onClick={() => { setEditingId(bu.id); setEditCode(bu.code); setEditName(bu.name); setEditFY(bu.activated_fy || ""); setEditStartMonth(bu.start_fiscal_month ?? null); }}
                        >
                          <span className="material-icons">edit</span>
                        </button>
                        <button
                          className="admin-btn admin-btn--icon admin-btn--danger"
                          title="Delete business unit"
                          onClick={() => handleDelete(bu.id, bu.name)}
                        >
                          <span className="material-icons">delete</span>
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {units.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: "center", padding: 20, color: "#9ca3af" }}>No business units defined yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminBusinessUnits;
